import 'react-toastify/dist/ReactToastify.css';

import { Auth } from '@aws-amplify/auth';
import { ThemeProvider } from '@mui/material';
import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import CaloLoader from './components/CaloLoader';
import PolicyGate from './components/PolicyGate';
import { useAuth } from './hooks/useAuth';
import { useIntercom } from './hooks/useIntercom';
import { usePolicyAcceptance } from './hooks/usePolicyAcceptance';
import { Country } from './lib/enum';
import { isPolicyExemptAffiliate } from './lib/policy';
import { useAuthStore } from './lib/store/authStore';
import { caloTheme } from './theme/calo';
import HomePage from './views/HomePage/HomePage';
import SignInForm from './views/signIn/SignInForm';
import useAffiliateProfile from './views/transactionsPage/useAffiliateProfile';

function App() {
  const { userId, setUserId } = useAuthStore();
  const { bootIntercom } = useIntercom();
  const { hasAccepted, acceptPolicy } = usePolicyAcceptance(userId);
  // Country comes from the affiliate's backend record, not the login-screen flag/phone
  // prefix, which is just a UI convenience and can't be trusted to reflect market.
  // Shares a query key with TransactionsCard, so react-query issues a single request.
  const { profile, profileFetched } = useAffiliateProfile({
    affiliateId: userId ?? ''
  });
  const isSaudiAffiliate = profile?.country?.toUpperCase() === Country.SA;
  const needsPolicyAcceptance =
    isSaudiAffiliate && !hasAccepted && !isPolicyExemptAffiliate(userId);

  useAuth({ userId, setUserId });

  useEffect(() => {
    const initializeIntercom = async () => {
      if (userId) {
        const authenticatedUser = await Auth.currentAuthenticatedUser();
        if (authenticatedUser) {
          bootIntercom({
            id: authenticatedUser.attributes.sub,
            name: authenticatedUser.attributes.name,
            email: authenticatedUser.attributes.email,
            phone: authenticatedUser.attributes.phone_number,
            page_title: 'calo-affiliates-portal'
          });
        }
      }
    };

    initializeIntercom();
  }, [bootIntercom, userId]);

  return (
    <ThemeProvider theme={caloTheme}>
      <Routes>
        <Route path='/uk' element={<SignInForm />} />
        <Route
          path='/'
          element={
            userId ? <Navigate to={`/${userId}`} replace /> : <SignInForm />
          }
        />
        {userId && (
          <Route
            path='/:id'
            element={
              // Block only until the profile query has settled once. Gating on
              // loading/error state instead would unmount HomePage whenever a retry
              // starts — and remounting it remounts the same profile query, which
              // refetches, which flips this gate again: an endless request loop.
              profileFetched ? (
                needsPolicyAcceptance ? (
                  <PolicyGate affiliateId={userId} onAccept={acceptPolicy} />
                ) : (
                  <HomePage userId={userId} />
                )
              ) : (
                <CaloLoader />
              )
            }
          />
        )}
        <Route path='*' element={<Navigate to='/' replace />} />
      </Routes>
      <ToastContainer position='top-right' />
    </ThemeProvider>
  );
}

export default App;
